import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from '@angular/router';
import { PageService } from './page.service';
import { catchError, Observable, of, tap } from 'rxjs';
import { SettingsService } from '../settings.service';
import { CacheService } from '../cache.service';
import { CACHE_KEYS } from '../../constants/cache-keys';
import { LoginResponse, LoginResults, LoginSelections } from '../../../login/login.viewmodel';
import { COMPONENT_META_DATA } from '../../constants/component-meta-data';
import { Language } from '../../components/language-switcher/language-switcher.model';
import { environment } from '../../../../environments/environment';
import { isLPMIHost } from '../../util/utils';

@Injectable({
  providedIn: 'root',
})
export class PageDataResolver implements Resolve<any> {
  constructor(private pageService: PageService, private settingsService: SettingsService, private cacheService: CacheService) { }

  private getCachedPagePath(path: string, appName?: string): string {
    const pagePathContext = this.cacheService.getFromCache(CACHE_KEYS.NAVIGATION.PAGE_PATH_CONTEXT);

    if (
      pagePathContext?.routePath === path
      && (pagePathContext?.appName ?? undefined) === (appName ?? undefined)
      && pagePathContext?.pagePath
    ) {
      return pagePathContext.pagePath;
    }

    return path;
  }

  resolve(route: ActivatedRouteSnapshot): Observable<any> {
    let path = route.routeConfig.path.split('/')[0];
    const appName = route.paramMap.get('appName');

    // AUTH page path is intentionally not resolved from page JSON.
    if (path === COMPONENT_META_DATA.AUTH.KEY) {
      return of({});
    }

    // Reuse a cached page-path mapping only when the current route and appName still match.
    // This keeps reloads stable for remapped URLs such as help-area-info/:appName while
    // preventing stale values from leaking into landing or other resolver calls.
    path = this.getCachedPagePath(path, appName);
    const resolvedPagePath = this.getPagePath(path, appName);

    if (path === COMPONENT_META_DATA.DEALER_HELP_DESK.KEY) {
      this.cacheService.addToCache(CACHE_KEYS.HELP_AREA_INFO.REDIRECT_URL, resolvedPagePath);
    }

    this.cacheService.addToCache(CACHE_KEYS.ARTICLE.APP_NAME, appName);
    return this.pageService.getPageData(resolvedPagePath).pipe(
      tap((response) => {
        this.pageService.setPageData(response);
      }),
      catchError((x) => {
        return of({});
      })
    );
  }

  getPagePath(path: string, appName?: string): string {
    let loginSelections: LoginSelections = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LOGIN_SELECTIONS);
    let region = loginSelections?.region;
    let businessOp = loginSelections?.selectedBusinessLabel?.key;
    let businessUnit = loginSelections?.isAdmin ? 'admin' : businessOp;
    let countryCode = 'us'; // default fallback
    if (loginSelections?.market) {
      let userMarket = loginSelections.market.toLowerCase();
      let authoredLanguages = this.cacheService.getFromCache(
        CACHE_KEYS.LOGIN.AUTHORED_LANGUAGES
      ) as Language[];

      // check if authoredLanguages contains the userMarket
      const isValidMarket = authoredLanguages?.some(
        (lang) => lang.countryCode?.toLowerCase() === userMarket
      );

      // if valid → use it; else fallback stays as 'us' or 'gb' based on region
      if(region === 'ee'){
        countryCode = isValidMarket ? userMarket : 'uk';
      }
      else{
        countryCode = isValidMarket ? userMarket : 'us';
      }
    }
    let language = this.pageService.getPreferredLanguage().languageCode;

    switch (path) {
      case `${COMPONENT_META_DATA.LOGIN.KEY}`:
        return `${this.settingsService.NUC_PAGE_PATH_PREFIX}/global/${language}/login`
      case `${COMPONENT_META_DATA.LANDING.KEY}`:
        return `${this.settingsService.NUC_PAGE_PATH_PREFIX}/${region}/${countryCode}/${language}/${businessUnit}/landingPage`;
      case `${COMPONENT_META_DATA.ARTICLE.KEY}`:
        return `${this.settingsService.NUC_PAGE_PATH_PREFIX}/${region}/${countryCode}/${language}/articles`;
      case `${COMPONENT_META_DATA.NOTIFICATIONS.KEY}`:
        return `${this.settingsService.NUC_PAGE_PATH_PREFIX}/${region}/${countryCode}/${language}/notifications`;
      case `${COMPONENT_META_DATA.USER_PREFERENCES.KEY}`:
        return `${this.settingsService.NUC_PAGE_PATH_PREFIX}/${region}/${countryCode}/${language}/user-preferences`;
      case `${COMPONENT_META_DATA.FAQS.KEY}`:
        const faqAppName = appName?.toLowerCase() === 'none' ? undefined : appName;
        return `${this.settingsService.NUC_PAGE_PATH_PREFIX}/${region}/${countryCode}/${language}/${businessUnit}/faq${faqAppName ? `/${faqAppName}` : ''}`;
      case `${COMPONENT_META_DATA.SITE_STATUS.KEY}`:
        return `${this.settingsService.NUC_PAGE_PATH_PREFIX}/global/${language}/site-status`;
      // case `${COMPONENT_META_DATA.AUTH.KEY}`:
      //   return `${this.settingsService.NUC_PAGE_PATH_PREFIX}/global/${language}/authorization`;
      case `${COMPONENT_META_DATA.NEWS_ANNOUNCEMENTS.KEY}`:
        return `${this.settingsService.NUC_PAGE_PATH_PREFIX}/${region}/${countryCode}/${language}/${businessUnit}/news-announcements`;
      case `${COMPONENT_META_DATA.HELP_AREA_INFO.KEY}`:
        return `${this.settingsService.NUC_PAGE_PATH_PREFIX}/${region}/${countryCode}/${language}/${businessUnit}/help-area-info/${appName}`;
      case `${COMPONENT_META_DATA.DEALER_HELP_DESK.KEY}`:
        return `${this.settingsService.NUC_PAGE_PATH_PREFIX}/${region}/${countryCode}/${language}/${businessUnit}/dealer-help-desk/${appName}`;
      case `${COMPONENT_META_DATA.WARRANTY_SUPPORT.KEY}`:
        return `${this.settingsService.NUC_PAGE_PATH_PREFIX}/${region}/${countryCode}/${language}/${businessUnit}/warranty-support/${appName}`;
      case `${COMPONENT_META_DATA.HQ_IMPORTERS.KEY}`:
        return `${this.settingsService.NUC_PAGE_PATH_PREFIX}/${region}/${countryCode}/${language}/${businessUnit}/hq-importers/${appName}`;
      case `${COMPONENT_META_DATA.HELP_AREA_ARTICLE_INFO.KEY}`:
        return `${this.settingsService.NUC_PAGE_PATH_PREFIX}/${region}/${countryCode}/${language}/help-area-info-article`;
      case `${COMPONENT_META_DATA.INFO_MANUAL.KEY}`:
        return `${this.settingsService.NUC_PAGE_PATH_PREFIX}/${region}/${countryCode}/${language}/info-manual`;
      default:
        return ``;
    }
  }
}
